/** venture/FundStatusForm.tsx — 결산보고용 자금운용현황(별지 제9호) 입력 → 생성 → 열람·다운로드 (2026-09-24)
 *  자동값(출자액·전환사채·미투자자산·법인 행)은 회색 안내값으로 보이고, 칸을 채우면 그 값이 우선한다. 원 단위. */
import React, { useState } from 'react';
import { mutStore, getTxt, getUp, getUpKey } from './stepcommon';
import { genFundStatus } from './api';
import { openS3, saveS3ToProject } from './docgen';
import { fmtComma, onlyDigits } from './bondcalc';
import { FS_IN, FS_OUT, fsKey, fsTxt, fundAuto, fundBody, uninvested } from './fundbody';
import { vicsSlot } from './vics13';
import type { Project, CoCard } from './types';

interface Props { p: Project; y: number; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; }

export default function FundStatusForm({ p, y, onPatch, toast }: Props) {
  const [busy, setBusy] = useState(false);
  const au = fundAuto(p, y);
  const set = (k: string, v: string) => onPatch(mutStore(p, 's15', (s) => { s.txt = s.txt || {}; if (v) s.txt[fsKey(y, k)] = v; else delete s.txt[fsKey(y, k)]; }));
  const amt = (k: string, ph: number | string = 0) => (
    <input className="cotext amt" inputMode="numeric" value={fmtComma(fsTxt(p, y, k))} placeholder={typeof ph === 'number' ? fmtComma(String(ph)) : ph} onChange={(e) => set(k, onlyDigits(e.target.value))} />
  );
  const txt = (k: string, ph: string) => <input className="cotext" value={fsTxt(p, y, k)} placeholder={ph} onChange={(e) => set(k, e.target.value)} />;
  const pdfName = getUp(p, 's15', 'fs_' + y + '_pdf'); const pdfKey = getUpKey(p, 's15', 'fs_' + y + '_pdf');
  const docName = getUp(p, 's15', 'fs_' + y + '_docx'); const docKey = getUpKey(p, 's15', 'fs_' + y + '_docx');
  const cos: CoCard[] = (p.collect && p.collect.cos) || [];

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      toast('공식 양식(별지 제9호)에 값을 채우는 중…');
      const { result } = await genFundStatus(p.id, fundBody(p, y));
      onPatch(mutStore(p, 's15', (s) => {
        s.upKey = s.upKey || {};
        if (result.s3pdf) { s.up['fs_' + y + '_pdf'] = result.pdf; s.upKey['fs_' + y + '_pdf'] = result.s3pdf; }
        if (result.s3doc) { s.up['fs_' + y + '_docx'] = result.doc; s.upKey['fs_' + y + '_docx'] = result.s3doc; }
        else { delete s.up['fs_' + y + '_docx']; delete s.upKey['fs_' + y + '_docx']; }
      }));
      toast('자금운용현황 완성 · 「PDF 보기」로 확인 후 VICS [1340]에 첨부하세요');
    } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="fsform">
      <div className="codoc-h">
        <span>📊 자금운용현황 (별지 제9호) 입력 <span className="s2tag">공식 양식 PDF의 빈칸만 채웁니다 · 원 단위 · 비우면 0 · 회색은 자동값</span></span>
        <button type="button" className="btn pri sm" disabled={busy} onClick={generate}>{busy ? '채우는 중…' : (pdfName || docName ? '🔄 양식 다시 채우기' : '📄 양식 채우기 (PDF)')}</button>
      </div>
      {pdfName || docName ? (
        <div className="s3file ok" style={{ marginTop: 6 }}>
          <span className="rf">📄 {pdfName || docName}</span><span className="pill p-green">생성됨</span><span className="grow" />
          {pdfKey ? <button type="button" className="rbtn" onClick={() => openS3(pdfKey, pdfName, true).catch((e) => toast((e as Error).message))}>👁 PDF 보기</button> : null}
          {pdfKey ? <button type="button" className="rbtn" onClick={() => void saveS3ToProject(p, pdfKey, pdfName, toast)}>⬇ PDF</button> : null}
          {docKey ? <button type="button" className="rbtn" onClick={() => void saveS3ToProject(p, docKey, docName, toast)}>⬇ Word</button> : null}
        </div>
      ) : null}
      <div className="fsgrid">
        <div className="fsh"><span>항목</span><span>전년말까지 누계</span><span>금년도 실적 ({y}년)</span><span>내역 / 비고</span></div>
        <div className="fsr"><span className="fl">출자액</span>{amt('chul_prev', au.chul[0])}{amt('chul_cur', au.chul[1])}<span className="s3muted">결성연도에 출자총액 전액 (증액 시 입력)</span></div>
        <div className="fsr"><span className="fl">전환사채 (투자)</span>{amt('cb_prev', au.cb[0])}{amt('cb_cur', au.cb[1])}<span className="s3muted">법인별 투자금액 · 사채 발행일 연도로 자동 분리</span></div>
        <div className="fsg">수익</div>
        {FS_IN.map(([k, label]) => <div className="fsr" key={k}><span className="fl">{label}</span>{amt(k + '_prev')}{amt(k + '_cur')}{txt(k + '_d2', '금년도 내역 (예: 예금이자)')}</div>)}
        <div className="fsg">지출</div>
        {FS_OUT.map(([k, label]) => <div className="fsr" key={k}><span className="fl">{label}</span>{amt(k + '_prev')}{amt(k + '_cur')}{txt(k + '_n', '비고')}</div>)}
      </div>
      <div className="lbl" style={{ marginTop: 10 }}>미투자자산 운용내역 <span className="s2tag">조합통장 잔액 등 · 합계 = 출자·수익 − 투자·지출</span></div>
      <div className="fsr four"><span className="fl">일반 금융상품</span>{txt('gen_name', '보통예금 (조합통장)')}{txt('gen_org', '예치기관 (은행명)')}
        <input className="cotext" type="date" value={fsTxt(p, y, 'gen_date')} onChange={(e) => set('gen_date', e.target.value)} title="취득(예치)일 · 비우면 결성등록일" />
        {amt('gen_bal', uninvested(p, y))}</div>
      <div className="fsr four"><span className="fl">기타</span>{txt('etc_name', '상품명 (없으면 비움)')}{txt('etc_org', '예치기관')}
        <input className="cotext" type="date" value={fsTxt(p, y, 'etc_date')} onChange={(e) => set('etc_date', e.target.value)} />
        {amt('etc_bal')}</div>
      <div className="lbl" style={{ marginTop: 10 }}>투자현황 · 법인별 <span className="s2tag">설립일·벤처확인번호는 13단계 VICS 1313 입력값</span></div>
      {cos.length ? cos.map((co) => {
        const founded = getTxt(p, 's13', vicsSlot(co.id, 'founded')); const vno = getTxt(p, 's13', vicsSlot(co.id, 'vno'));
        const row = au.rows.find((r) => r.name === (co.name || ''));
        return (
          <div className="fsr co" key={co.id}>
            <span className="fl">🏢 {co.name || '(법인)'}</span>
            <span className="s3muted">투자 {row ? fmtComma(String(row.cb)) : '0'}원 · {row && row.date ? row.date : '발행일 미입력'}</span>
            <span className="s3muted">{founded ? '설립 ' + founded : '설립일 미입력'} · {vno || '확인번호 미입력'}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span className="s3muted">납입자본금</span>{amt('co_' + co.id + '_capital', '원')}</span>
          </div>
        );
      }) : <div className="s3muted">등록된 투자법인이 없습니다</div>}
    </div>
  );
}
